import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/gameConfig';

/**
 * Player: The player's ship entity.
 * Handles movement, shooting, respawning, and invulnerability.
 */
export class Player {
  private scene: Phaser.Scene;
  private sprite!: Phaser.Physics.Arcade.Sprite;
  private glowSprite!: Phaser.GameObjects.Image;
  private thrusterSprite!: Phaser.GameObjects.Image;
  private lastFireTime = 0;
  private invulnerable = false;
  private blinkTimer?: Phaser.Time.TimerEvent;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  create(x: number, y: number): Phaser.Physics.Arcade.Sprite {
    // Create glow sprite behind the player (neon cyan glow effect)
    this.glowSprite = this.scene.add.image(x, y, 'player');
    this.glowSprite.setScale(1.4);
    this.glowSprite.setAlpha(0.2);
    this.glowSprite.setTint(0x00ffff);
    this.glowSprite.setBlendMode(Phaser.BlendModes.ADD);
    this.glowSprite.setDepth(4);

    // Subtle pulsing glow animation
    this.scene.tweens.add({
      targets: this.glowSprite,
      alpha: { from: 0.15, to: 0.35 },
      scale: { from: 1.3, to: 1.5 },
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Thruster/engine glow below the player ship
    this.thrusterSprite = this.scene.add.image(x, y + 16, 'thruster-glow');
    this.thrusterSprite.setBlendMode(Phaser.BlendModes.ADD);
    this.thrusterSprite.setAlpha(0.8);
    this.thrusterSprite.setDepth(3); // Behind glow and player
    this.thrusterSprite.setScale(1.2, 1.4);

    // Thruster flicker animation - rapid oscillation for engine glow feel
    this.scene.tweens.add({
      targets: this.thrusterSprite,
      alpha: { from: 0.55, to: 0.95 },
      scaleY: { from: 1.2, to: 1.8 },
      scaleX: { from: 1.0, to: 1.3 },
      duration: 100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.sprite = this.scene.physics.add.sprite(x, y, 'player');
    this.sprite.setCollideWorldBounds(true);
    this.sprite.setDepth(5);
    return this.sprite;
  }

  update(cursors: Phaser.Types.Input.Keyboard.CursorKeys, time: number): void {
    // Movement
    if (cursors.left.isDown) {
      this.sprite.setVelocityX(-GAME_CONFIG.player.speed);
    } else if (cursors.right.isDown) {
      this.sprite.setVelocityX(GAME_CONFIG.player.speed);
    } else {
      this.sprite.setVelocityX(0);
    }
  }

  canFire(time: number): boolean {
    return time - this.lastFireTime > GAME_CONFIG.player.fireRate;
  }

  fire(time: number): Phaser.Physics.Arcade.Sprite | null {
    if (!this.canFire(time)) return null;
    this.lastFireTime = time;

    const bullet = this.scene.physics.add.sprite(
      this.sprite.x,
      this.sprite.y - 20,
      'bullet-player'
    );
    bullet.setVelocityY(-400);
    return bullet;
  }

  getSprite(): Phaser.Physics.Arcade.Sprite {
    return this.sprite;
  }

  getGlowSprite(): Phaser.GameObjects.Image {
    return this.glowSprite;
  }

  /** Sync the glow sprite and thruster position to follow the player sprite */
  syncGlow(): void {
    if (this.glowSprite?.active && this.sprite?.active) {
      this.glowSprite.x = this.sprite.x;
      this.glowSprite.y = this.sprite.y;
    }
    if (this.thrusterSprite?.active && this.sprite?.active) {
      this.thrusterSprite.x = this.sprite.x;
      this.thrusterSprite.y = this.sprite.y + 16;
    }
  }

  /** Get the thruster glow sprite */
  getThrusterSprite(): Phaser.GameObjects.Image {
    return this.thrusterSprite;
  }

  setInvulnerable(duration: number): void {
    this.invulnerable = true;

    // Blinking effect during invulnerability for clear visual feedback
    let blinkVisible = true;
    this.blinkTimer = this.scene.time.addEvent({
      delay: 100,
      callback: () => {
        if (this.sprite?.active) {
          blinkVisible = !blinkVisible;
          this.sprite.setAlpha(blinkVisible ? 0.8 : 0.2);
        }
      },
      loop: true,
    });

    // End invulnerability after duration
    this.scene.time.delayedCall(duration, () => {
      this.invulnerable = false;
      if (this.blinkTimer) {
        this.blinkTimer.destroy();
        this.blinkTimer = undefined;
      }
      if (this.sprite?.active) {
        this.sprite.setAlpha(1);
      }
    });
  }

  isInvulnerable(): boolean {
    return this.invulnerable;
  }

  destroy(): void {
    // Clean up blink timer from invulnerability
    if (this.blinkTimer) {
      this.blinkTimer.destroy();
      this.blinkTimer = undefined;
    }
    if (this.glowSprite) {
      this.scene.tweens.killTweensOf(this.glowSprite);
      this.glowSprite.destroy();
    }
    if (this.thrusterSprite) {
      this.scene.tweens.killTweensOf(this.thrusterSprite);
      this.thrusterSprite.destroy();
    }
    if (this.sprite) {
      this.scene.tweens.killTweensOf(this.sprite);
      this.sprite.destroy();
    }
  }
}
